'use client';

import type { CSSProperties } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

export type LensPromotionLabel = {
  promotion_label?: string | number | null;
  promotionLabel?: string | number | null;
  short_promotion_label?: string | number | null;
  shortPromotionLabel?: string | number | null;
  display_logic?: string | number | null;
  displayLogic?: string | number | null;
  base_lowest_promotion_price?: string | number | null;
  baseLowestPromotionPrice?: string | number | null;
  base_lens_lowest_promotion_price?: string | number | null;
  baseLensLowestPromotionPrice?: string | number | null;
  lowest_promotion_price?: string | number | null;
  lowestPromotionPrice?: string | number | null;
  min_lens_promotion_price?: string | number | null;
  minLensPromotionPrice?: string | number | null;
  min_promotion_price?: string | number | null;
  minPromotionPrice?: string | number | null;
  lens_type_name?: string | number | null;
  lensTypeName?: string | number | null;
  lens_type_title?: string | number | null;
  lensTypeTitle?: string | number | null;
  lens_name?: string | number | null;
  lensName?: string | number | null;
  refractive_index_name?: string | number | null;
  refractiveIndexName?: string | number | null;
  lens_index_name?: string | number | null;
  lensIndexName?: string | number | null;
  index_name?: string | number | null;
  indexName?: string | number | null;
  refractive_index?: string | number | null;
  refractiveIndex?: string | number | null;
  max_discount_percentage?: string | number | null;
  maxDiscountPercentage?: string | number | null;
  highest_discount_percentage?: string | number | null;
  highestDiscountPercentage?: string | number | null;
  discount_percentage?: string | number | null;
  discountPercentage?: string | number | null;
  discount_percent?: string | number | null;
  discountPercent?: string | number | null;
  max_discount_amount?: string | number | null;
  maxDiscountAmount?: string | number | null;
  max_discount_price?: string | number | null;
  maxDiscountPrice?: string | number | null;
  base_max_discount_amount?: string | number | null;
  baseMaxDiscountAmount?: string | number | null;
  base_max_discount_price?: string | number | null;
  baseMaxDiscountPrice?: string | number | null;
  discount_amount?: string | number | null;
  discountAmount?: string | number | null;
};

interface SelectLensPromotionTagProps {
  className?: string;
  label?: LensPromotionLabel | null;
}

const PROMOTION_TEXT_SEPARATOR = '\n';

const MEASURE_STYLE: CSSProperties = {
  left: -9999,
  maxWidth: 'none',
  overflow: 'visible',
  pointerEvents: 'none',
  position: 'fixed',
  textOverflow: 'clip',
  top: -9999,
  visibility: 'hidden',
  whiteSpace: 'nowrap',
  width: 'max-content',
  zIndex: -1,
};

const normalizeText = (value: unknown) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return undefined;
  }

  const text = String(value).trim();

  return text.length > 0 ? text : undefined;
};

const getUniqueTexts = (texts: Array<string | undefined>): string[] =>
  Array.from(new Set(texts.filter((text): text is string => Boolean(text))));

const resolveDisplayLogic = (label?: LensPromotionLabel | null) => {
  const displayLogic = Number(label?.displayLogic ?? label?.display_logic);

  return Number.isFinite(displayLogic) ? displayLogic : undefined;
};

const extractPercentText = (text?: string) => {
  const match = text?.match(/(\d+(?:\.\d+)?)\s*%/);

  return match?.[1] ? `Up to ${match[1]}% off` : undefined;
};

const extractPercentValue = (text?: string) => {
  const match = text?.match(/(\d+(?:\.\d+)?)\s*%/);

  return match?.[1];
};

const extractSaveAmount = (text?: string) => {
  const match = text?.match(/\bSave\s+(.+?)\s+today\b/i);

  return normalizeText(match?.[1]);
};

const getFirstTextByKeys = (label: LensPromotionLabel | null | undefined, keys: string[]) => {
  const record = label as Record<string, unknown> | null | undefined;

  for (const key of keys) {
    const text = normalizeText(record?.[key]);

    if (text) {
      return text;
    }
  }

  return undefined;
};

const resolveLensTypeName = (label?: LensPromotionLabel | null) =>
  getFirstTextByKeys(label, [
    'lensTypeName',
    'lens_type_name',
    'lensTypeTitle',
    'lens_type_title',
    'lensName',
    'lens_name',
    'lensType',
    'lens_type',
  ]);

const resolveRefractiveIndexName = (label?: LensPromotionLabel | null) =>
  getFirstTextByKeys(label, [
    'refractiveIndexName',
    'refractive_index_name',
    'lensIndexName',
    'lens_index_name',
    'indexName',
    'index_name',
    'refractiveIndex',
    'refractive_index',
  ]);

const resolveDiscountPercentage = (
  label: LensPromotionLabel | null | undefined,
  promotionTexts: Array<string | undefined>,
) =>
  getFirstTextByKeys(label, [
    'maxDiscountPercentage',
    'max_discount_percentage',
    'highestDiscountPercentage',
    'highest_discount_percentage',
    'discountPercentage',
    'discount_percentage',
    'discountPercent',
    'discount_percent',
  ])?.replace(/%.*$/, '') ?? promotionTexts.map(extractPercentValue).find(Boolean);

const resolveBaseLowestPromotionPrice = (label?: LensPromotionLabel | null) =>
  normalizeText(label?.baseLowestPromotionPrice) ??
  normalizeText(label?.base_lowest_promotion_price) ??
  normalizeText(label?.baseLensLowestPromotionPrice) ??
  normalizeText(label?.base_lens_lowest_promotion_price) ??
  normalizeText(label?.lowestPromotionPrice) ??
  normalizeText(label?.lowest_promotion_price) ??
  normalizeText(label?.minLensPromotionPrice) ??
  normalizeText(label?.min_lens_promotion_price) ??
  normalizeText(label?.minPromotionPrice) ??
  normalizeText(label?.min_promotion_price);

const resolveMaxDiscountAmount = (
  label: LensPromotionLabel | null | undefined,
  promotionTexts: Array<string | undefined>,
) =>
  getFirstTextByKeys(label, [
    'baseMaxDiscountAmount',
    'base_max_discount_amount',
    'baseMaxDiscountPrice',
    'base_max_discount_price',
    'maxDiscountAmount',
    'max_discount_amount',
    'maxDiscountPrice',
    'max_discount_price',
    'discountAmount',
    'discount_amount',
  ]) ?? promotionTexts.map(extractSaveAmount).find(Boolean);

const buildMode1Text = (label?: LensPromotionLabel | null) => {
  const baseLowestPromotionPrice = resolveBaseLowestPromotionPrice(label);

  return baseLowestPromotionPrice ? `From ${baseLowestPromotionPrice}` : undefined;
};

const buildMode2Text = (
  label: LensPromotionLabel | null | undefined,
  promotionTexts: Array<string | undefined>,
) => {
  const discountPercentage = resolveDiscountPercentage(label, promotionTexts);

  return discountPercentage ? `Up to ${discountPercentage}% off` : undefined;
};

const buildMode3Or5Text = (
  lensName: string | undefined,
  label: LensPromotionLabel | null | undefined,
  promotionTexts: Array<string | undefined>,
) => {
  const maxDiscountAmount = resolveMaxDiscountAmount(label, promotionTexts);

  return lensName && maxDiscountAmount
    ? `${lensName} (Save ${maxDiscountAmount} today)`
    : undefined;
};

const buildMode4Or6Text = (
  lensName: string | undefined,
  label: LensPromotionLabel | null | undefined,
  promotionTexts: Array<string | undefined>,
) => {
  const discountPercentage = resolveDiscountPercentage(label, promotionTexts);

  return lensName && discountPercentage
    ? `${lensName} (${discountPercentage}% off today)`
    : undefined;
};

const resolveGeneratedFallbackTexts = (
  label?: LensPromotionLabel | null,
  promotionTexts: Array<string | undefined> = [],
) => {
  const displayLogic = resolveDisplayLogic(label);
  const mode1Text = buildMode1Text(label);
  const mode2Text = buildMode2Text(label, promotionTexts);
  const lensTypeName = resolveLensTypeName(label);
  const refractiveIndexName = resolveRefractiveIndexName(label);

  if (displayLogic === 1) {
    return [mode1Text];
  }

  if (displayLogic === 2) {
    return [mode2Text];
  }

  if (displayLogic === 3) {
    return [buildMode3Or5Text(lensTypeName, label, promotionTexts), mode1Text];
  }

  if (displayLogic === 4) {
    return [buildMode4Or6Text(lensTypeName, label, promotionTexts), mode2Text];
  }

  if (displayLogic === 5) {
    return [buildMode3Or5Text(refractiveIndexName, label, promotionTexts), mode1Text];
  }

  if (displayLogic === 6) {
    return [buildMode4Or6Text(refractiveIndexName, label, promotionTexts), mode2Text];
  }

  return [mode2Text ?? promotionTexts.map(extractPercentText).find(Boolean), mode1Text];
};

export const resolveLensPromotionLabelTexts = (label?: LensPromotionLabel | null): string[] => {
  const promotionText =
    normalizeText(label?.promotionLabel) ?? normalizeText(label?.promotion_label);
  const shortPromotionText =
    normalizeText(label?.shortPromotionLabel) ?? normalizeText(label?.short_promotion_label);
  const sourceTexts = [promotionText, shortPromotionText];

  return getUniqueTexts([
    promotionText,
    shortPromotionText,
    ...resolveGeneratedFallbackTexts(label, sourceTexts),
  ]);
};

const getCssMaxWidth = (maxWidth: string, parentWidth: number) => {
  if (!maxWidth || maxWidth === 'none') {
    return Number.POSITIVE_INFINITY;
  }

  if (maxWidth.endsWith('%')) {
    const percent = Number.parseFloat(maxWidth);

    return Number.isFinite(percent) ? (parentWidth * percent) / 100 : Number.POSITIVE_INFINITY;
  }

  const width = Number.parseFloat(maxWidth);

  return Number.isFinite(width) ? width : Number.POSITIVE_INFINITY;
};

export default function SelectLensPromotionTag({ className, label }: SelectLensPromotionTagProps) {
  const promotionTexts = useMemo(() => resolveLensPromotionLabelTexts(label), [label]);
  const [fittedText, setFittedText] = useState('');
  const tagRef = useRef<HTMLSpanElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (promotionTexts.length === 0) {
      setFittedText('');
      return;
    }

    const tagElement = tagRef.current;
    const measureElement = measureRef.current;

    if (!tagElement || !measureElement) {
      setFittedText(promotionTexts[0]);
      return;
    }

    const updateFittedText = () => {
      const parentWidth = tagElement.parentElement?.getBoundingClientRect().width ?? 0;
      const tagWidth = tagElement.getBoundingClientRect().width;
      const computedStyle = window.getComputedStyle(tagElement);
      const cssMaxWidth = getCssMaxWidth(computedStyle.maxWidth, parentWidth);
      const availableWidth = Number.isFinite(cssMaxWidth) ? cssMaxWidth : tagWidth || parentWidth;

      if (!availableWidth) {
        setFittedText(promotionTexts[0]);
        return;
      }

      const nextText =
        promotionTexts.find((text) => {
          measureElement.textContent = text;
          const measuredWidth =
            measureElement.getBoundingClientRect().width || measureElement.scrollWidth;

          return measuredWidth <= Math.ceil(availableWidth);
        }) ?? promotionTexts[promotionTexts.length - 1];

      setFittedText(nextText);
    };

    updateFittedText();

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateFittedText);
    const parentElement = tagElement.parentElement;

    if (resizeObserver) {
      resizeObserver.observe(tagElement);

      if (parentElement) {
        resizeObserver.observe(parentElement);
      }
    }

    window.addEventListener('resize', updateFittedText);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateFittedText);
    };
  }, [promotionTexts]);

  const displayText = fittedText || promotionTexts[0] || '';

  useLayoutEffect(() => {
    if (!displayText || promotionTexts.length < 2) {
      return;
    }

    const tagElement = tagRef.current;

    if (!tagElement || tagElement.scrollWidth <= tagElement.clientWidth + 1) {
      return;
    }

    const currentIndex = promotionTexts.indexOf(displayText);
    const nextIndex = currentIndex >= 0 ? currentIndex + 1 : 1;
    const nextText = promotionTexts[nextIndex];

    if (nextText) {
      setFittedText(nextText);
    }
  }, [displayText, promotionTexts]);

  if (!displayText) {
    return null;
  }

  return (
    <>
      <span ref={tagRef} className={className}>
        {displayText}
      </span>
      <span ref={measureRef} aria-hidden="true" className={className} style={MEASURE_STYLE}>
        {promotionTexts.join(PROMOTION_TEXT_SEPARATOR)}
      </span>
    </>
  );
}
